import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import { Plus, Check, Calendar, Trash2, Share2, X, ChevronRight, Lock, Users, Zap } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// ── Share Modal ─────────────────────────────────────────────────────────────
function ShareModal({ task, teamMembers, onClose, onShared }) {
  const [selected, setSelected] = useState(task.shared_with || []);
  const toggle = (id) => setSelected(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const save = async () => {
    try {
      await axios.put(`${API}/api/my-tasks/${task.id}/share`, { user_ids: selected });
      toast.success(`Shared with ${selected.length} people`);
      onShared();
      onClose();
    } catch (err) { toast.error("Failed to share"); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: "#fff", borderRadius: "12px", padding: "20px", width: "360px", maxHeight: "70vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px" }}>
          <h3 style={{ margin: 0, fontSize: "15px", fontWeight: "800", color: "#1B2B22" }}>Share Task</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={16} /></button>
        </div>
        <div style={{ maxHeight: "250px", overflowY: "auto", border: "1px solid #E2E8F0", borderRadius: "7px" }}>
          {teamMembers.map(m => (
            <div key={m.id} onClick={() => toggle(m.id)}
              style={{ display: "flex", alignItems: "center", gap: "8px", padding: "8px 12px", cursor: "pointer", background: selected.includes(m.id) ? "#ECFDF5" : "#fff", borderBottom: "1px solid #F1F5F9" }}>
              <div style={{ width: "16px", height: "16px", borderRadius: "4px", border: selected.includes(m.id) ? "none" : "1.5px solid #D1D5DB", background: selected.includes(m.id) ? "#2F7D52" : "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                {selected.includes(m.id) && <Check size={10} color="#fff" />}
              </div>
              <span style={{ fontSize: "12px", color: "#1B2B22" }}>{m.full_name || m.email}</span>
            </div>
          ))}
        </div>
        <button onClick={save} style={{ marginTop: "12px", width: "100%", padding: "8px", borderRadius: "8px", border: "none", background: "#2F7D52", color: "#fff", fontSize: "12px", cursor: "pointer", fontWeight: "700" }}>
          Save
        </button>
      </div>
    </div>
  );
}

// ── Task Row ────────────────────────────────────────────────────────────────
function TaskRow({ task, source, onToggle, onDelete, onShare }) {
  const sourceColors = { private: { bg: "#F5F3FF", color: "#7C3AED", label: "Private" }, pulse: { bg: "#FEF3C7", color: "#92400E", label: task.pulse_name || "Pulse" }, project: { bg: "#EFF6FF", color: "#1D4ED8", label: task.project_name || "Project" } };
  const s = sourceColors[source] || sourceColors.private;
  const overdue = task.due_date && !task.is_done && new Date(task.due_date) < new Date();

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px 14px", background: "#fff", border: "1px solid #E2E8F0", borderRadius: "8px", marginBottom: "5px" }}>
      {onToggle ? (
        <div onClick={onToggle} data-testid={`toggle-task-${task.id}`}
          style={{ width: "18px", height: "18px", borderRadius: "4px", border: task.is_done ? "none" : "1.5px solid #D1D5DB", background: task.is_done ? "#2F7D52" : "#fff", cursor: "pointer", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {task.is_done && <Check size={12} color="#fff" />}
        </div>
      ) : (
        <div style={{ width: "18px", height: "18px", borderRadius: "4px", background: task.is_done ? "#2F7D52" : "#E2E8F0", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {task.is_done && <Check size={12} color="#fff" />}
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: "13px", fontWeight: "600", color: task.is_done ? "#94A3B8" : "#1B2B22", textDecoration: task.is_done ? "line-through" : "none" }}>{task.title}</div>
        <div style={{ display: "flex", gap: "6px", marginTop: "2px", alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: "9px", padding: "1px 6px", borderRadius: "4px", background: s.bg, color: s.color, fontWeight: "700" }}>{s.label}</span>
          {task.due_date && (
            <span style={{ fontSize: "10px", color: overdue ? "#DC2626" : "#64748B", display: "flex", alignItems: "center", gap: "2px" }}>
              <Calendar size={9} />{new Date(task.due_date).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
            </span>
          )}
          {task.assigned_to_name && <span style={{ fontSize: "10px", color: "#64748B" }}>→ {task.assigned_to_name}</span>}
          {source === "private" && task.shared_with?.length > 0 && (
            <span style={{ fontSize: "9px", color: "#8B5CF6", display: "flex", alignItems: "center", gap: "2px" }}><Users size={9} />{task.shared_with.length} shared</span>
          )}
        </div>
      </div>
      {source === "private" && onShare && (
        <button onClick={onShare} style={{ background: "none", border: "none", cursor: "pointer", color: "#94A3B8", padding: "4px" }} title="Share">
          <Share2 size={13} />
        </button>
      )}
      {source === "project" && task.project_id && (
        <Link to={`/app/projects/${task.project_id}`} style={{ color: "#94A3B8", padding: "4px" }} title="Go to project">
          <ChevronRight size={14} />
        </Link>
      )}
      {onDelete && (
        <button onClick={onDelete} style={{ background: "none", border: "none", cursor: "pointer", color: "#D1D5DB", padding: "4px" }}>
          <Trash2 size={13} />
        </button>
      )}
    </div>
  );
}

// ── Main Page ───────────────────────────────────────────────────────────────
export default function MyTasksPage() {
  const { user } = useAuth();
  const [data, setData] = useState({ private_tasks: [], pulse_tasks: [], project_tasks: [] });
  const [loading, setLoading] = useState(true);
  const [newTitle, setNewTitle] = useState("");
  const [newDue, setNewDue] = useState("");
  const [shareTask, setShareTask] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [filter, setFilter] = useState("all");

  const fetchAll = useCallback(async () => {
    try {
      const [tasksRes, usersRes] = await Promise.all([
        axios.get(`${API}/api/my-tasks`),
        axios.get(`${API}/api/users`),
      ]);
      setData(tasksRes.data);
      setTeamMembers(usersRes.data.filter(u => u.id !== user?.id));
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [user?.id]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const createTask = async () => {
    if (!newTitle.trim()) return;
    try {
      await axios.post(`${API}/api/my-tasks`, { title: newTitle, due_date: newDue || null });
      setNewTitle(""); setNewDue("");
      fetchAll();
    } catch (err) { toast.error("Failed to create task"); }
  };

  const togglePrivate = async (task) => {
    try {
      await axios.put(`${API}/api/my-tasks/${task.id}`, { is_done: !task.is_done });
      fetchAll();
    } catch (err) { console.error(err); }
  };

  const deletePrivate = async (task) => {
    try {
      await axios.delete(`${API}/api/my-tasks/${task.id}`);
      fetchAll();
    } catch (err) { console.error(err); }
  };

  const allTasks = useMemo(() => [
    ...data.private_tasks.map(t => ({ ...t, _source: "private" })),
    ...data.pulse_tasks.map(t => ({ ...t, _source: "pulse" })),
    ...data.project_tasks.map(t => ({ ...t, _source: "project" })),
  ], [data]);
  const filtered = useMemo(() => filter === "all" ? allTasks : allTasks.filter(t => t._source === filter), [allTasks, filter]);
  const pending = useMemo(() => filtered.filter(t => !t.is_done), [filtered]);
  const done = useMemo(() => filtered.filter(t => t.is_done), [filtered]);

  const counts = { all: allTasks.length, private: data.private_tasks.length, pulse: data.pulse_tasks.length, project: data.project_tasks.length };

  if (loading) return <div style={{ padding: "24px", color: "#94A3B8" }}>Loading...</div>;

  return (
    <div style={{ padding: "20px 24px", fontFamily: "Manrope, sans-serif", maxWidth: "800px" }} data-testid="my-tasks-page">
      <h1 style={{ fontSize: "22px", fontWeight: "800", color: "#1B2B22", margin: "0 0 4px" }}>My Tasks</h1>
      <p style={{ fontSize: "12px", color: "#64748B", marginBottom: "16px" }}>{pending.length} pending · {done.length} completed</p>

      {/* Add private task */}
      <div style={{ display: "flex", gap: "6px", marginBottom: "16px", alignItems: "center", background: "#F5F8F5", padding: "10px 12px", borderRadius: "8px" }}>
        <Lock size={13} style={{ color: "#8B5CF6", flexShrink: 0 }} />
        <input value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="Add a private task..."
          onKeyDown={e => e.key === "Enter" && createTask()}
          style={{ flex: 1, padding: "7px 10px", borderRadius: "6px", border: "1px solid #D1D5DB", fontSize: "12px" }} />
        <input type="date" value={newDue} onChange={e => setNewDue(e.target.value)}
          style={{ padding: "6px 8px", borderRadius: "6px", border: "1px solid #D1D5DB", fontSize: "11px" }} />
        <button onClick={createTask} data-testid="add-private-task-btn"
          style={{ padding: "7px 14px", borderRadius: "6px", background: "#2F7D52", color: "#fff", border: "none", fontSize: "11px", cursor: "pointer", fontWeight: "600" }}>Add</button>
      </div>

      {/* Filter tabs */}
      <div style={{ display: "flex", gap: "4px", marginBottom: "14px" }}>
        {[["all", "All"], ["private", "Private"], ["pulse", "Pulse"], ["project", "Projects"]].map(([key, label]) => (
          <button key={key} onClick={() => setFilter(key)}
            style={{ padding: "5px 12px", borderRadius: "6px", border: filter === key ? "1px solid #2F7D52" : "1px solid #E2E8F0",
              background: filter === key ? "#ECFDF5" : "#fff", color: filter === key ? "#2F7D52" : "#64748B",
              fontSize: "11px", fontWeight: "600", cursor: "pointer" }}>
            {label} ({counts[key]})
          </button>
        ))}
      </div>

      {/* Pending tasks */}
      {pending.length === 0 && done.length === 0 ? (
        <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: "10px", padding: "48px", textAlign: "center" }}>
          <Check size={32} style={{ color: "#94A3B8", margin: "0 auto 12px" }} />
          <p style={{ fontSize: "14px", color: "#64748B" }}>No tasks here yet</p>
        </div>
      ) : (
        <>
          {pending.map(t => (
            <TaskRow key={`${t._source}-${t.id}`} task={t} source={t._source}
              onToggle={t._source === "private" ? () => togglePrivate(t) : undefined}
              onDelete={t._source === "private" ? () => deletePrivate(t) : undefined}
              onShare={t._source === "private" ? () => setShareTask(t) : undefined} />
          ))}
          {done.length > 0 && (
            <div style={{ marginTop: "16px" }}>
              <div style={{ fontSize: "11px", fontWeight: "700", color: "#94A3B8", textTransform: "uppercase", marginBottom: "6px" }}>Completed ({done.length})</div>
              {done.map(t => (
                <TaskRow key={`${t._source}-${t.id}`} task={t} source={t._source}
                  onToggle={t._source === "private" ? () => togglePrivate(t) : undefined}
                  onDelete={t._source === "private" ? () => deletePrivate(t) : undefined} />
              ))}
            </div>
          )}
        </>
      )}

      {shareTask && <ShareModal task={shareTask} teamMembers={teamMembers} onClose={() => setShareTask(null)} onShared={fetchAll} />}
    </div>
  );
}
